import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { useMemo, useState } from 'react'
import { InfoTooltip } from '../InfoTooltip/InfoTooltip'
import cx from 'classnames'

function CustomTick(props: {
  x: number | string
  y: number | string
  payload: { value: string | number; index?: number; dataLegnth?: number }
  currencyFormatter: (value: number) => string
}) {
  const {
    x,
    y,
    payload: { value },
    currencyFormatter
  } = props
  return (
    <text
      x={Number(x) + 7}
      y={Number(y)}
      className="text-text-secondary text-right text-xs"
      fill="currentColor"
      textAnchor="end"
    >
      {currencyFormatter(Number(value))}
    </text>
  )
}

export function EstimatedCbamLiabilityChart({
  language,
  strings,
  data
}: {
  data: { year: number; liability: number }[]
  language: string
  strings: {
    liabilityTooltip: string
    title: string
    noGoods: string
  }
}) {
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const currencyFormatter2Decimals = useMemo(() => {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2
    }).format
  }, [language])

  const currencyFormatterNoDecimals = useMemo(() => {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format
  }, [language])

  const currentYear = data[activeIndex]?.year
  const currentLiability = data[activeIndex]?.liability
    ? currencyFormatter2Decimals(data[activeIndex]?.liability)
    : '--'

  const hasNoGoods = data.length === 0

  return (
    <div className="border border-gray-400">
      <div className="border-b border-gray-400 rounded-xs p-1.5 flex items-center flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <p className="font-bold text-xl">{currentLiability}</p>
          <InfoTooltip content={strings.liabilityTooltip} />
        </div>
        <p className="text-xs text-gray-500">
          {strings.title} {currentYear}
        </p>
      </div>
      <div className="relative **:outline-none">
        {hasNoGoods && (
          <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
            <p className="text-xs text-gray-500">{strings.noGoods}</p>
          </div>
        )}
        <ResponsiveContainer className="bg-gray-300" width="100%" height={220}>
          <BarChart data={data} margin={{ top: 25, right: 30, bottom: 0, left: 0 }}>
            <XAxis hide dataKey="year" />
            <YAxis
              className="text-gray-300"
              scale="linear"
              stroke="currentColor"
              dataKey="liability"
              domain={[0, 'dataMax + 30']}
              tick={props => (
                <CustomTick {...props} currencyFormatter={currencyFormatterNoDecimals} />
              )}
            />
            <Bar
              dataKey="liability"
              isAnimationActive={false}
              onClick={(_, index) => setActiveIndex(index)}
            >
              <LabelList
                dataKey="year"
                position="top"
                fill="currentColor"
                className="text-text-secondary"
                fontSize={14}
                fontWeight={600}
                strokeWidth={0}
              />
              {data.map(({ year }, index) => (
                <Cell
                  cursor="pointer"
                  className={cx(
                    index === activeIndex ? 'text-green-500 stroke-green-600' : 'text-blue-300'
                  )}
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth={1}
                  key={`cell-${year}`}
                  radius={4}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
